import { useEffect, useState } from 'react';
import type { InvitePreview } from '@scribbleplot/shared';
import { ApiError, api } from '../lib/api';
import { useSession } from '../lib/session';
import type { Navigate } from '../lib/router';
import { color, font } from '../tokens';
import { PENDING_INVITE_KEY } from '../lib/pendingInvite';
import { AccentButton, GhostButton, Spinner } from '../ui/primitives';

/**
 * Landing page for an invite link.
 *
 * Shows what is being offered before anything is accepted, so the recipient
 * knows which board and which role. A signed-out visitor is sent to sign in and
 * comes straight back — the token stays in the URL, so nothing is lost.
 */
export function InviteScreen({ token, accent, navigate }: { token: string; accent: string; navigate: Navigate }) {
  const user = useSession((s) => s.user);
  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .previewInvite(token)
      .then(({ preview: p }) => !cancelled && setPreview(p))
      .catch((err) => !cancelled && setError(err instanceof ApiError ? err.message : 'This invite link is not valid.'));
    return () => {
      cancelled = true;
    };
  }, [token]);

  const accept = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { boardId } = await api.acceptInvite(token);
      navigate({ name: 'board', id: boardId }, { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not join this board.');
      setBusy(false);
    }
  };

  const shell = (children: React.ReactNode) => (
    <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div
        style={{
          width: 400,
          background: color.surface,
          border: `1px solid ${color.lineSoft}`,
          borderRadius: 18,
          padding: '30px 28px',
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
          textAlign: 'center',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, justifyContent: 'center' }}>
          <div style={{ width: 18, height: 18, borderRadius: 5, background: accent }} />
          <div style={{ fontSize: 14, fontWeight: 700 }}>Scribbleplot</div>
        </div>
        {children}
      </div>
    </div>
  );

  if (error) {
    return shell(
      <>
        <div style={{ fontSize: 18, fontWeight: 700 }}>This link has expired</div>
        <div style={{ fontSize: 13, color: color.textMuted, lineHeight: 1.6 }}>{error}</div>
        <GhostButton onClick={() => navigate({ name: user ? 'home' : 'landing' })}>
          {user ? 'Go to your boards' : 'Go to scribbleplot.com'}
        </GhostButton>
      </>,
    );
  }

  if (!preview) return <Spinner accent={accent} label="Checking invite" />;

  const roleLabel = preview.role === 'editor' ? 'edit' : 'view';

  if (preview.alreadyOwner) {
    return shell(
      <>
        <div style={{ fontSize: 18, fontWeight: 700 }}>This is your own board</div>
        <div style={{ fontSize: 13, color: color.textMuted, lineHeight: 1.6 }}>
          You already own <strong style={{ color: color.text }}>{preview.boardName}</strong>. Send this link to whoever
          you want to invite.
        </div>
        <AccentButton accent={accent} onClick={() => navigate({ name: 'home' })}>
          Go to your boards
        </AccentButton>
      </>,
    );
  }

  // Not signed in: remember the token so signing in returns here, rather than
  // dropping the invitee on their empty board list.
  if (!user) {
    try {
      sessionStorage.setItem(PENDING_INVITE_KEY, token);
    } catch {
      // Private browsing may refuse storage; the link still works if reopened.
    }
    return shell(
      <>
        <div style={{ fontFamily: font.mono, fontSize: 10.5, letterSpacing: '.14em', color: accent }}>
          YOU HAVE BEEN INVITED
        </div>
        <div style={{ fontSize: 19, fontWeight: 700, letterSpacing: '-.01em' }}>{preview.boardName}</div>
        <div style={{ fontSize: 13, color: color.textMuted, lineHeight: 1.6 }}>
          Sign in or create an account to {roleLabel} this board. You will come straight back here.
        </div>
        <AccentButton accent={accent} onClick={() => navigate({ name: 'signup' })}>
          Create an account
        </AccentButton>
        <GhostButton onClick={() => navigate({ name: 'signin' })}>I already have one</GhostButton>
      </>,
    );
  }

  return shell(
    <>
      <div style={{ fontFamily: font.mono, fontSize: 10.5, letterSpacing: '.14em', color: accent }}>
        YOU HAVE BEEN INVITED
      </div>
      <div style={{ fontSize: 19, fontWeight: 700, letterSpacing: '-.01em' }}>{preview.boardName}</div>
      <div style={{ fontSize: 13, color: color.textMuted, lineHeight: 1.6 }}>
        You will be able to <strong style={{ color: color.text }}>{roleLabel}</strong> this board.
      </div>
      <AccentButton accent={accent} onClick={() => void accept()} disabled={busy}>
        {busy ? 'Joining…' : `Join as ${preview.role}`}
      </AccentButton>
      <GhostButton onClick={() => navigate({ name: 'home' })}>Not now</GhostButton>
    </>,
  );
}
